import { useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_get } from '@zg-rentals/rental-manager-env';

import { useSelector } from '../../../../selectors/useSelector';

import CostsAndFeesSFCSEvents from '../../../../model/ClickstreamEvent/CostsAndFeesSFCSEvents';

import clickstreamUtils from '../../../../utils/clickstreamUtils';

import SingleFamilyCostsAndFeesModal from '../../../Modal/SingleFamilyCostsAndFeesModal';

import { FeesByCategory } from './FeesByCategory';

import type { SingleFamilyFeeDto } from '@zg-rentals/rental-manager-env';

import { TRIGGER_LOCATIONS_MAP } from '../constants';

const CostsAndFeesFormField = ({ displayedLocation }: { displayedLocation: string }) => {
  const { currentProperty, listingAlias, user } = useSelector((state) => {
    return {
      currentProperty: state.properties.currentProperty,
      listingAlias: state.properties.currentProperty.listingAlias,
      user: state.user,
    };
  }, shallowEqual);

  const { active, dateLastActivatedTimestamp } = currentProperty;

  const [showCostsAndFeesModal, setShowCostsAndFeesModal] = useState(false);
  const [feeToEdit, setFeeToEdit] = useState<SingleFamilyFeeDto | null>(null);
  const [fixedCategory, setFixedCategory] = useState('');

  const { data } = useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_get({
    listingAlias,
  });

  const triggerLocation = useMemo(() => {
    const mappedLocation = TRIGGER_LOCATIONS_MAP[displayedLocation as keyof typeof TRIGGER_LOCATIONS_MAP];
    const listingStatusSuffix = clickstreamUtils.getListingStatusSuffix(active, dateLastActivatedTimestamp);

    return `${mappedLocation}${listingStatusSuffix}`;
  }, [active, dateLastActivatedTimestamp, displayedLocation]);

  if (!data) {
    return null;
  }

  const handleAddFeeClick = (fixedCategory = '') => {
    setShowCostsAndFeesModal(true);
    setFixedCategory(fixedCategory);
    CostsAndFeesSFCSEvents.ClickAddFee({ currentProperty, user, triggerLocation });
  };

  const handleEditFeeClick = (fee: SingleFamilyFeeDto) => {
    setFeeToEdit(fee);
    setFixedCategory(fee.feeCategory);
    setShowCostsAndFeesModal(true);
    CostsAndFeesSFCSEvents.ClickEditFee({ currentProperty, user, triggerLocation });
  };

  const handleCloseFeeModal = () => {
    setShowCostsAndFeesModal(false);
    setFeeToEdit(null);
    setFixedCategory('');
  };

  return (
    <div data-testid="CostsAndFeesFormField">
      <FeesByCategory
        existingFees={data.existingFees}
        dictionaryItems={data.dictionaryItems}
        handleAddFeeClick={handleAddFeeClick}
        handleEditFeeClick={handleEditFeeClick}
        displayedLocation={displayedLocation}
      />

      {showCostsAndFeesModal && (
        <SingleFamilyCostsAndFeesModal
          onClose={handleCloseFeeModal}
          feeToEdit={feeToEdit}
          fixedCategory={feeToEdit?.feeCategory || fixedCategory}
        />
      )}
    </div>
  );
};

export default CostsAndFeesFormField;
